import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Instance, SESSION_EXPIRED } from '../services/apiService';
import ConfirmDialog from '../components/ConfirmDialog';
import Monitoring from '../components/Monitoring';

// Live timer component for running instances
const LiveQuotaTimer: React.FC<{ instance: Instance; totalHours: number }> = ({ instance, totalHours }) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    if (instance.State !== 'running') return;
    
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [instance.State]);

  const calculateRuntime = () => {
    const totalSeconds = instance.RuntimeSeconds || 0;
    const lastStart = instance.LastStartTime || 0;
    
    if (instance.State === 'running' && lastStart > 0) {
      const currentSession = Math.floor((currentTime / 1000) - lastStart);
      return totalSeconds + currentSession;
    }
    return totalSeconds;
  };

  const runtimeSeconds = calculateRuntime();
  const hours = Math.floor(runtimeSeconds / 3600);
  const minutes = Math.floor((runtimeSeconds % 3600) / 60);
  const seconds = runtimeSeconds % 60;
  const usedHours = runtimeSeconds / 3600;
  const percentage = (usedHours / totalHours) * 100;
  const isOverQuota = usedHours >= totalHours;
  const isWarning = percentage > 80;
  
  // Format quota hours properly
  const quotaHours = Math.floor(totalHours);
  const quotaMinutes = Math.floor((totalHours % 1) * 60);

  return (
    <div style={{ minWidth: '180px' }}>
      <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.4rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
        <i className="fas fa-stopwatch" style={{marginRight: '0.3rem'}}></i> {hours}h {minutes}m {seconds}s / {quotaHours}h {quotaMinutes > 0 ? `${quotaMinutes}m` : ''}
      </div>
      <div style={{
        width: '100%',
        height: '4px',
        background: '#F5F7FA',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${Math.min(percentage, 100)}%`,
          height: '100%',
          background: isOverQuota
            ? 'linear-gradient(135deg, #FF6600, #FF8533)'
            : isWarning
            ? 'linear-gradient(135deg, #FF6600, #FFAA33)'
            : 'linear-gradient(135deg, #10b981, #059669)',
          borderRadius: '2px',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const { userEmail, logout } = useAuth();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'instances' | 'students' | 'quota' | 'monitoring'>('instances');
  const [categoryTab, setCategoryTab] = useState<'Tutorials' | 'Assignment1' | 'Assignment2'>('Tutorials');
  // Table sorting. null = keep the order the API returned.
  const [sortKey, setSortKey] = useState<'name' | 'state' | 'quota' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [ipAddress, setIpAddress] = useState('');
  const [ipLoading, setIpLoading] = useState(false);
  const [quotaEmail, setQuotaEmail] = useState('');
  const [quotaHours, setQuotaHours] = useState('');
  const [quotaInstance, setQuotaInstance] = useState<Instance | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<any>(null);
  const [runtimeData, setRuntimeData] = useState<any[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    instanceId: string;
    action: 'start' | 'stop' | 'force-stop';
    instanceName: string;
  }>({
    isOpen: false,
    instanceId: '',
    action: 'start',
    instanceName: ''
  });
  
  // Students modal for viewing team members
  const [studentsModal, setStudentsModal] = useState<{
    isOpen: boolean;
    instanceName: string;
    students: Array<{email: string; name: string}>;
  }>({
    isOpen: false,
    instanceName: '',
    students: []
  });

  // Add QUT brand styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      body { 
        background: linear-gradient(135deg, #003A70 0%, #0066CC 50%, #4A90E2 100%);
        min-height: 100vh;
        position: relative;
        overflow-x: hidden;
      }
      body::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: 
          radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.4) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(240, 147, 251, 0.2) 0%, transparent 50%);
        pointer-events: none;
        z-index: -1;
        animation: float 15s ease-in-out infinite;
      }
      body::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
        background-size: 100px 100px;
        animation: drift 25s linear infinite;
        pointer-events: none;
        z-index: -1;
        opacity: 0.6;
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(180deg); }
      }
      @keyframes drift {
        0% { transform: translateX(0px); }
        100% { transform: translateX(-100px); }
      }
      @keyframes glow {
        0%, 100% { box-shadow: 0 0 30px rgba(102, 126, 234, 0.4); }
        50% { box-shadow: 0 0 60px rgba(102, 126, 234, 0.7), 0 0 80px rgba(240, 147, 251, 0.5); }
      }
      .modern-btn:not(:disabled):hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15) !important;
      }
      .start-btn:not(:disabled):hover {
        background: linear-gradient(135deg, #0066CC, #4A90E2) !important;
      }
      .stop-btn:not(:disabled):hover {
        background: linear-gradient(135deg, #FF8533, #FF6600) !important;
      }
      .modern-btn:active { transform: translateY(0); }
      .glass-card {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.1));
        backdrop-filter: blur(30px);
        border: 2px solid rgba(255, 255, 255, 0.4);
        box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5);
        position: relative;
        overflow: hidden;
        animation: glow 4s ease-in-out infinite;
      }
      .glass-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(240,147,251,0.1) 50%, rgba(255,255,255,0.1) 100%);
        pointer-events: none;
      }
      .modern-table tr:hover {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(240, 147, 251, 0.1));
        transform: translateY(-3px);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .modern-btn {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
      }
      .modern-btn::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
        transition: left 0.5s;
      }
      .modern-btn:hover::before {
        left: 100%;
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.9; transform: scale(1.05); }
      }
      .force-stop-btn:not(:disabled):hover {
        background: linear-gradient(135deg, #B91C1C, #DC2626) !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    fetchData();
    fetchQuotaInfo();
    fetchRuntimeData();
  }, []);

  // Auto-refresh every 5 minutes when any instance is running
  useEffect(() => {
    const hasRunningInstances = instances.some(instance => instance.State === 'running');
    if (!hasRunningInstances) return;

    const intervalId = setInterval(() => {
      fetchData();
    }, 300000); // 5 minutes

    return () => clearInterval(intervalId);
  }, [instances]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const instancesData = await apiService.fetchInstances();
      setInstances(instancesData);

      // Fetch students from Cognito
      await fetchStudents();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      alert(`Failed to fetch data: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await apiService.fetchStudents();
      setStudents(response);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const handleInstanceAction = async (instanceId: string, action: 'start' | 'stop' | 'force-stop') => {
    const instance = instances.find(i => i.InstanceId === instanceId);
    setConfirmDialog({
      isOpen: true,
      instanceId,
      action,
      instanceName: instance?.Name || instanceId
    });
  };

  const confirmAction = async () => {
    const { instanceId, action } = confirmDialog;
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      setActionLoading(`${instanceId}-${action}`);
      
      if (action === 'start') {
        await apiService.startInstance(instanceId);
      } else if (action === 'force-stop') {
        await apiService.forceStopInstance(instanceId);
      } else {
        await apiService.stopInstance(instanceId);
      }
      
      await fetchData();
    } catch (error: any) {
      console.error(`Failed to ${action} instance:`, error);
      // Expired session already redirects to /login; don't mislead with "try again".
      if ((error as any)?.message === SESSION_EXPIRED) {
        alert('Your session has expired. Please log in again.');
      } else {
        alert(`Failed to ${action} instance. ${action === 'stop' ? 'Try using Force Stop.' : 'Please try again.'}`);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const cancelAction = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };



  const handleDownloadKeypair = async (instanceId: string) => {
    try {
      if (!userEmail) {
        alert('User email not available');
        return;
      }
      
      // Call Lambda function directly using AWS SDK invoke
      const response = await fetch('https://0oto2a7yqe.execute-api.ap-southeast-2.amazonaws.com/prod/keypair', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'download',
          instanceId,
          studentEmail: userEmail
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error('Invalid response from server');
      }

      if (!response.ok || data.error) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Download the keypair file using the pre-signed URL
      const keypairResponse = await fetch(data.downloadUrl);
      if (!keypairResponse.ok) {
        throw new Error('Failed to download keypair file from S3');
      }
      
      const keypairContent = await keypairResponse.text();
      
      // Create and download file
      const blob = new Blob([keypairContent], { type: 'application/x-pem-file' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.keypairName}.pem`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      alert(`Keypair downloaded! Set permissions: chmod 400 ${data.keypairName}.pem`);
      
    } catch (error) {
      console.error('Failed to download keypair:', error);
      alert(`Failed to download keypair: ${error}`);
    }
  };

  const handleAddIP = async () => {
    if (!selectedInstance) {
      alert('No instance selected');
      return;
    }

    let finalIpAddress = ipAddress.trim();
    
    // If no IP provided, try to auto-detect
    if (!finalIpAddress) {
      try {
        finalIpAddress = await apiService.getMyIP();
        setIpAddress(finalIpAddress); // Update the input field
      } catch (error) {
        alert('Could not detect your IP address. Please enter it manually.');
        return;
      }
    }

    // Validate IP format
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(finalIpAddress)) {
      alert('Please enter a valid IP address (e.g., 192.168.1.100)');
      return;
    }

    try {
      setIpLoading(true);
      await apiService.addIPToSecurityGroup(selectedInstance.InstanceId, finalIpAddress);
      alert(`Successfully added ${finalIpAddress}/32 to security group for SSH and RDP access`);
      setIpAddress('');
    } catch (error) {
      console.error('Failed to add IP:', error);
      alert(`Failed to add IP: ${error}`);
    } finally {
      setIpLoading(false);
    }
  };

  // Auto-detect IP when modal opens
  const handleInstanceSelect = async (instance: Instance) => {
    setSelectedInstance(instance);
    try {
      const detectedIP = await apiService.getMyIP();
      setIpAddress(detectedIP);
    } catch (error) {
      // IP auto-detection failed, user will enter manually
      setIpAddress('');
    }
  };

  // Effective quota usage as a fraction of allowance, matching what LiveQuotaTimer
  // displays: banked seconds plus the in-progress session if the instance is running.
  // Using a fraction (not raw seconds) keeps the order correct when students have
  // different quotas after a top-up.
  const getQuotaFraction = (instance: Instance) => {
    const banked = instance.RuntimeSeconds || 0;
    const lastStart = instance.LastStartTime || 0;
    const live = instance.State === 'running' && lastStart > 0
      ? Math.max(0, Math.floor(Date.now() / 1000) - lastStart)
      : 0;
    const quotaSeconds = (instance.QuotaHours || 30) * 3600;
    return quotaSeconds > 0 ? (banked + live) / quotaSeconds : 0;
  };

  // Active states first when ascending, so an admin sees live machines at the top.
  const STATE_RANK: Record<string, number> = {
    running: 0, pending: 1, stopping: 2, 'shutting-down': 3, stopped: 4, terminated: 5
  };

  const handleSort = (key: 'name' | 'state' | 'quota') => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Most useful first press: highest quota usage, most active state, names A-Z.
      setSortDir(key === 'name' ? 'asc' : key === 'state' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (key: 'name' | 'state' | 'quota') => {
    if (sortKey !== key) return 'fas fa-sort';
    return sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  };

  const getFilteredInstances = () => {
    const filtered = instances.filter(instance => {
      const tags = instance.Tags || [];
      const categoryTag = tags.find(tag => tag.Key === 'Category');
      return categoryTag?.Value === categoryTab;
    });

    if (!sortKey) return filtered;

    const dir = sortDir === 'asc' ? 1 : -1;
    // Copy first: never sort the state array in place.
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = (a.Name || '').localeCompare(b.Name || '', undefined, { sensitivity: 'base' });
      } else if (sortKey === 'state') {
        const ra = STATE_RANK[a.State] ?? 99;
        const rb = STATE_RANK[b.State] ?? 99;
        cmp = ra - rb;
        // Same state: fall back to name so the order is stable and predictable.
        if (cmp === 0) cmp = (a.Name || '').localeCompare(b.Name || '', undefined, { sensitivity: 'base' });
      } else {
        cmp = getQuotaFraction(a) - getQuotaFraction(b);
        if (cmp === 0) cmp = (a.Name || '').localeCompare(b.Name || '', undefined, { sensitivity: 'base' });
      }
      return cmp * dir;
    });
  };

  const getInstanceQuota = (instanceId: string) => {
    // Get quota directly from instance data (returned by ListInstances Lambda)
    const instance = instances.find(i => i.InstanceId === instanceId);
    if (!instance) return null;
    
    const usedHours = instance.RuntimeHours || 0;
    const totalHours = instance.QuotaHours || 10;
    
    return { usedHours, totalHours };
  };

  const getButtonState = (instance: Instance, action: 'start' | 'stop') => {
    const isTransitioning = ['pending', 'stopping', 'starting'].includes(instance.State);
    const isLoading = actionLoading === `${instance.InstanceId}-${action}`;
    const quota = getInstanceQuota(instance.InstanceId);
    const isOverQuota = quota && quota.usedHours > quota.totalHours;
    

    
    if (action === 'start') {
      return {
        disabled: Boolean(instance.State !== 'stopped' || isTransitioning || isLoading || isOverQuota),
        text: isOverQuota ? <><i className="fas fa-ban"></i> Quota Exceeded</> : isLoading ? <><i className="fas fa-spinner fa-spin"></i> Starting...</> : <><i className="fas fa-play"></i> Start</>,
        className: 'btn btn-success btn-sm'
      };
    } else {
      return {
        disabled: Boolean(instance.State !== 'running' || isTransitioning || isLoading),
        text: isLoading ? <><i className="fas fa-spinner fa-spin"></i> Stopping...</> : <><i className="fas fa-stop"></i> Stop</>,
        className: 'btn btn-danger btn-sm'
      };
    }
  };

  const handleQuotaManage = async (instance: Instance) => {
    setQuotaInstance(instance);
    
    try {
      const quota = await apiService.getQuotaInfo();
      setQuotaInfo(quota);
    } catch (error) {
      console.error('Failed to fetch quota info:', error);
    }
  };

  const fetchQuotaInfo = async () => {
    try {
      const quota = await apiService.getQuotaInfo();
      setQuotaInfo(quota);
    } catch (error) {
      console.error('Failed to fetch quota info:', error);
    }
  };

  const fetchRuntimeData = async () => {
    try {
      const quota = await apiService.getQuotaInfo();
      if (quota && quota.instancesQuota) {
        // Convert instancesQuota object to array
        const allInstances = Object.values(quota.instancesQuota).map((instance: any) => ({
          instanceId: instance.instanceId,
          totalSeconds: instance.totalSeconds,
          quotaSeconds: instance.quotaSeconds
        }));
        setRuntimeData(allInstances);
      }
    } catch (error) {
      console.error('Failed to fetch runtime data:', error);
    }
  };

  const handleAddQuota = async () => {
    if (!quotaInstance || !quotaHours) {
      alert('Please select an instance and enter hours');
      return;
    }
    
    try {
      await apiService.addQuota(quotaInstance.InstanceId, parseInt(quotaHours));
      alert(`Added ${quotaHours} hours to instance ${quotaInstance.InstanceId}`);
      setQuotaHours('');
      setQuotaInstance(null);
      await fetchQuotaInfo();
      await fetchData();
      await fetchRuntimeData();
    } catch (error) {
      alert(`Failed to add quota: ${error}`);
    }
  };

  const handleRemoveQuota = async () => {
    if (!quotaInstance || !quotaHours) {
      alert('Please select an instance and enter hours');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to remove ${quotaHours} hours from ${quotaInstance.Name}?`)) {
      return;
    }
    
    try {
      await apiService.removeQuota(quotaInstance.InstanceId, parseInt(quotaHours));
      alert(`Removed ${quotaHours} hours from instance ${quotaInstance.InstanceId}`);
      setQuotaHours('');
      setQuotaInstance(null);
      await fetchQuotaInfo();
      await fetchData();
      await fetchRuntimeData();
    } catch (error) {
      alert(`Failed to remove quota: ${error}`);
    }
  };

  const handleDownloadSSHKey = async (instanceId: string) => {
    try {
      const token = sessionStorage.getItem('idToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://0oto2a7yqe.execute-api.ap-southeast-2.amazonaws.com/prod'}/ssh-key/${instanceId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get file: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Download the file
      const fileResponse = await fetch(data.downloadUrl);
      const fileContent = await fileResponse.text();
      
      // Determine file type and MIME type
      const isRdpFile = data.fileType === 'rdp' || data.keyName.endsWith('.rdp');
      const mimeType = isRdpFile ? 'application/x-rdp' : 'application/x-pem-file';
      
      // Create and download file
      const blob = new Blob([fileContent], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.keyName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      if (isRdpFile) {
        alert('RDP file downloaded! Double-click to connect. Use the Windows credentials provided by your teaching team.');
      } else {
        alert('SSH key downloaded! Set permissions: chmod 400 ' + data.keyName);
      }
    } catch (error) {
      alert(`Failed to download file: ${error}`);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#003A70',
            letterSpacing: '-0.02em',
            margin: 0
          }}>
            Admin Dashboard
          </h1>
          <p style={{
            fontSize: '1rem',
            color: '#6b7280',
            fontWeight: '400',
            margin: '0.5rem 0 0 0'
          }}>
            Manage student instances and resources
          </p>
        </div>
        <div className="admin-user-info" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2rem',
          position: 'relative',
          zIndex: 1
        }}>
          <span className="admin-user-email" style={{
            padding: '0.6rem 1.2rem',
            background: '#F5F7FA',
            borderRadius: '8px',
            color: '#495057',
            fontWeight: '500',
            fontSize: '0.9rem',
            border: '1px solid #dee2e6',
            display: 'inline-flex',
            alignItems: 'center',
            height: '38px',
            boxSizing: 'border-box'
          }}><i className="fas fa-user-shield"></i> {userEmail || 'Admin'}</span>
          <button onClick={logout} style={{
            padding: '0.6rem 1.2rem',
            background: '#FF6600',
            color: 'white',
            border: '1px solid #FF6600',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            height: '38px',
            boxSizing: 'border-box'
          }}>Logout</button>
        </div>
      </div>

      <div className="admin-content">
        <div className="stats-grid">
          <div className="stat-card card-gradient purple">
            <h3><i className="fas fa-server" style={{marginRight: '0.4rem'}}></i> Total Instances</h3>
            <p className="stat-value">{instances.length}</p>
          </div>
          <div className="stat-card card-gradient pink">
            <h3><i className="fas fa-play-circle" style={{marginRight: '0.4rem'}}></i> Active Instances</h3>
            <p className="stat-value">{instances.filter(i => i.State === 'running').length}</p>
          </div>
          <div className="stat-card card-gradient blue">
            <h3><i className="fas fa-stop-circle" style={{marginRight: '0.4rem'}}></i> Stopped Instances</h3>
            <p className="stat-value">{instances.filter(i => i.State === 'stopped').length}</p>
          </div>
          <div className="stat-card card-gradient sunset">
            <h3><i className="fas fa-users" style={{marginRight: '0.4rem'}}></i> Total Students</h3>
            <p className="stat-value">{students.length}</p>
          </div>
        </div>

        <div className="tab-navigation">
          {[
            { key: 'instances', icon: 'fas fa-server', label: 'All Instances', count: instances.length },
            { key: 'students', icon: 'fas fa-users', label: 'Students', count: students.length },
            { key: 'monitoring', icon: 'fas fa-chart-line', label: 'Monitoring', count: null }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
            >
              <i className={tab.icon} style={{marginRight: '0.4rem'}}></i> {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        <div className="card">
          {activeTab === 'instances' && (
            <div>
              <div className="content-header">
                <h2>EC2 Instances</h2>
                <button className="btn btn-primary" onClick={fetchData} disabled={loading}>
                  <i className="fas fa-sync-alt"></i> {loading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              
              {/* Category Tabs */}
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                marginBottom: '2rem',
                borderBottom: '1px solid #F5F7FA',
                paddingBottom: '0'
              }}>
                {(['Tutorials', 'Assignment1', 'Assignment2'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setCategoryTab(tab)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: categoryTab === tab ? '#003A70' : 'transparent',
                      color: categoryTab === tab ? 'white' : '#6b7280',
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      borderBottom: categoryTab === tab ? '3px solid #003A70' : '3px solid transparent'
                    }}
                  >
                    {tab === 'Assignment1' ? 'Assignment 1' : tab === 'Assignment2' ? 'Assignment 2' : tab}
                  </button>
                ))}
              </div>
              
              {loading ? (
                <div className="loading-state">Loading instances...</div>
              ) : getFilteredInstances().length === 0 ? (
                <div className="empty-state">No instances found for {categoryTab === 'Assignment1' ? 'Assignment 1' : categoryTab === 'Assignment2' ? 'Assignment 2' : categoryTab}.</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      {([
                        { key: 'name',  icon: 'fas fa-desktop',        label: 'INSTANCE',  sortable: true  },
                        { key: null,    icon: 'fas fa-users',          label: 'STUDENTS',  sortable: false },
                        { key: 'state', icon: 'fas fa-circle-notch',   label: 'STATE',     sortable: true  },
                        { key: null,    icon: 'fas fa-globe',          label: 'PUBLIC IP', sortable: false },
                        { key: 'quota', icon: 'fas fa-hourglass-half', label: 'QUOTA',     sortable: true  },
                        { key: null,    icon: 'fas fa-cogs',           label: 'ACTIONS',   sortable: false },
                      ] as const).map(col => {
                        const active = col.sortable && sortKey === col.key;
                        return (
                          <th
                            key={col.label}
                            onClick={col.sortable ? () => handleSort(col.key as 'name' | 'state' | 'quota') : undefined}
                            role={col.sortable ? 'button' : undefined}
                            tabIndex={col.sortable ? 0 : undefined}
                            aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                            title={col.sortable ? `Sort by ${col.label.toLowerCase()}` : undefined}
                            onKeyDown={col.sortable ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSort(col.key as 'name' | 'state' | 'quota');
                              }
                            } : undefined}
                            style={{
                              padding: '1rem 1.25rem',
                              textAlign: 'left',
                              fontWeight: '700',
                              color: active ? '#003A70' : '#374151',
                              fontSize: '0.8rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              cursor: col.sortable ? 'pointer' : 'default',
                              userSelect: 'none',
                              whiteSpace: 'nowrap',
                              borderBottom: active ? '2px solid #003A70' : '2px solid transparent'
                            }}
                          >
                            <i className={col.icon}></i> {col.label}
                            {col.sortable && (
                              <i
                                className={sortIcon(col.key as 'name' | 'state' | 'quota')}
                                style={{
                                  marginLeft: '0.45rem',
                                  fontSize: '0.75rem',
                                  opacity: active ? 1 : 0.35
                                }}
                              ></i>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredInstances().map(instance => (
                      <tr key={instance.InstanceId}>
                        <td style={{ padding: '1.5rem 1.25rem' }}>
                          <div style={{ 
                            fontWeight: '600', 
                            color: '#003A70',
                            fontSize: '1rem',
                            marginBottom: '0.25rem'
                          }}>
                            {instance.Name}
                          </div>
                          <div style={{ 
                            fontSize: '0.8rem', 
                            color: '#6b7280', 
                            fontFamily: 'monospace',
                            background: 'rgba(107, 114, 128, 0.1)',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '6px',
                            display: 'inline-block'
                          }}>
                            {instance.InstanceId}
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1.25rem', maxWidth: '200px' }}>
                          {instance.AssignedStudents && instance.AssignedStudents.length > 0 ? (
                            <button
                              onClick={() => setStudentsModal({
                                isOpen: true,
                                instanceName: instance.Name || instance.InstanceId,
                                students: instance.AssignedStudents || []
                              })}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                background: 'linear-gradient(135deg, rgba(0, 58, 112, 0.1), rgba(0, 102, 204, 0.1))',
                                border: '2px solid rgba(0, 58, 112, 0.2)',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: '#003A70'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 58, 112, 0.15), rgba(0, 102, 204, 0.15))';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 58, 112, 0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 58, 112, 0.1), rgba(0, 102, 204, 0.1))';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <i className="fas fa-users"></i>
                              <span>{instance.AssignedStudents.length} Student{instance.AssignedStudents.length !== 1 ? 's' : ''}</span>
                              <i className="fas fa-chevron-right" style={{ fontSize: '0.7rem', opacity: 0.6 }}></i>
                            </button>
                          ) : (
                            <span style={{ 
                              color: '#9ca3af', 
                              fontSize: '0.8rem',
                              fontStyle: 'italic'
                            }}>
                              No students
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '1.5rem 1.25rem' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.5rem 1rem',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            backgroundColor: 
                              instance.State === 'running' ? 'rgba(34, 197, 94, 0.1)' : 
                              instance.State === 'stopped' ? 'rgba(251, 191, 36, 0.1)' : 
                              'rgba(59, 130, 246, 0.1)',
                            color: 
                              instance.State === 'running' ? '#22c55e' : 
                              instance.State === 'stopped' ? '#fbbf24' : 
                              '#3b82f6',
                            border: `2px solid ${
                              instance.State === 'running' ? '#22c55e' : 
                              instance.State === 'stopped' ? '#fbbf24' : 
                              '#3b82f6'
                            }`,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            whiteSpace: 'nowrap'
                          }}>
                            <i className="fas fa-circle" style={{fontSize: '0.5rem', marginRight: '0.4rem', verticalAlign: 'middle'}}></i> {instance.State}
                          </span>
                        </td>
                        <td style={{ padding: '1.5rem 1.25rem' }}>
                          <div style={{
                            padding: '0.5rem 1rem',
                            background: instance.PublicIp ? 'rgba(34, 197, 94, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                            color: instance.PublicIp ? '#22c55e' : '#6b7280',
                            borderRadius: '12px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            fontFamily: 'monospace',
                            border: `1px solid ${instance.PublicIp ? 'rgba(34, 197, 94, 0.2)' : 'rgba(107, 114, 128, 0.2)'}`,
                            whiteSpace: 'nowrap'
                          }}>
                            <i className="fas fa-network-wired"></i> {instance.PublicIp || 'Not Available'}
                          </div>
                        </td>
                        <td style={{ padding: '1.5rem 1.25rem' }}>
                          {(() => {
                            const totalHours = instance.QuotaHours || 10;
                            return <LiveQuotaTimer instance={instance} totalHours={totalHours} />;
                          })()}
                        </td>

                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {(() => {
                              const startBtn = getButtonState(instance, 'start');
                              const stopBtn = getButtonState(instance, 'stop');
                              const showForceStop = instance.State === 'stopping';
                              
                              return (
                                <>
                                  <button 
                                    onClick={() => handleInstanceAction(instance.InstanceId, 'start')}
                                    disabled={startBtn.disabled}
                                    className="modern-btn start-btn"
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: startBtn.disabled
                                        ? 'linear-gradient(135deg, #F5F7FA, #d1d5db)' 
                                        : 'linear-gradient(135deg, #003A70, #0066CC)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '12px',
                                      cursor: startBtn.disabled ? 'not-allowed' : 'pointer',
                                      fontSize: '0.8rem',
                                      fontWeight: '600',
                                      boxShadow: startBtn.disabled
                                        ? 'none' 
                                        : '0 4px 12px rgba(79, 172, 254, 0.3)',
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.25rem'
                                    }}
                                  >
                                    {startBtn.text}
                                  </button>
                                  <button 
                                    onClick={() => handleInstanceAction(instance.InstanceId, 'stop')}
                                    disabled={stopBtn.disabled}
                                    className="modern-btn stop-btn"
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: stopBtn.disabled
                                        ? 'linear-gradient(135deg, #F5F7FA, #d1d5db)' 
                                        : 'linear-gradient(135deg, #FF6600, #FF8533)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '12px',
                                      cursor: stopBtn.disabled ? 'not-allowed' : 'pointer',
                                      fontSize: '0.8rem',
                                      fontWeight: '600',
                                      boxShadow: stopBtn.disabled
                                        ? 'none' 
                                        : '0 4px 12px rgba(255, 107, 107, 0.3)',
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.25rem'
                                    }}
                                  >
                                    {stopBtn.text}
                                  </button>
                                  {showForceStop && (
                                    <button 
                                      onClick={() => handleInstanceAction(instance.InstanceId, 'force-stop')}
                                      className="modern-btn force-stop-btn"
                                      style={{
                                        padding: '0.5rem 1rem',
                                        background: 'linear-gradient(135deg, #DC2626, #EF4444)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: '600',
                                        boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem'
                                      }}
                                    >
                                      <i className="fas fa-exclamation-triangle"></i> Force Stop
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                            <button 
                              onClick={() => handleDownloadSSHKey(instance.InstanceId)}
                              className="modern-btn keypair-btn"
                              style={{
                                padding: '0.5rem 1rem',
                                background: 'linear-gradient(135deg, #FF6600, #FFAA33)',
                                color: '#92400e',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                boxShadow: '0 4px 12px rgba(255, 217, 61, 0.3)',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 217, 61, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 217, 61, 0.3)';
                              }}
                            >
                              {(instance.Name?.includes('Windows') || instance.Name?.includes('windows') || instance.InstanceId === 'i-079d9443c979f9dd7') ? <><i className="fas fa-desktop"></i> Download RDP</> : <><i className="fas fa-key"></i> SSH Key</>}
                            </button>
                            <button 
                              onClick={() => handleInstanceSelect(instance)}
                              className="modern-btn details-btn"
                              style={{
                                padding: '0.5rem 1rem',
                                background: 'linear-gradient(135deg, #003A70, #0066CC)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(168, 85, 247, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(168, 85, 247, 0.3)';
                              }}
                            >
                              <i className="fas fa-sliders-h"></i> Manage
                            </button>
                            <button 
                              onClick={() => handleQuotaManage(instance)}
                              className="modern-btn quota-btn"
                              style={{
                                padding: '0.5rem 1rem',
                                background: 'linear-gradient(135deg, #0066CC, #4A90E2)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                              }}
                            >
                              <i className="fas fa-hourglass-half"></i> Quota
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'students' && (
            <div>
              <div className="content-header">
                <h2>Student Management</h2>
              </div>
              
              {loading ? (
                <div className="loading">Loading students...</div>
              ) : students.length === 0 ? (
                <div className="empty-state">No students found.</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => (
                      <tr key={student.email}>
                        <td>{student.email}</td>
                        <td>{student.name}</td>
                        <td>
                          <span className="badge badge-info">
                            {student.role}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            student.status === 'Active' ? 'badge-success' : 'badge-warning'
                          }`}>
                            {student.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'monitoring' && <Monitoring instances={instances} />}

          {activeTab === 'quota' && (
            <div>
              <div className="content-header">
                <h2><i className="fas fa-hourglass-half"></i> Quota Management</h2>
              </div>
              
              <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '16px',
                padding: '2rem',
                marginBottom: '2rem'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Add Quota Hours</h3>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#6b7280', fontSize: '0.9rem' }}>
                      Selected Instance
                    </label>
                    <div style={{
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      fontSize: '1rem'
                    }}>
                      {quotaInstance ? `${quotaInstance.Name} (${quotaInstance.InstanceId})` : 'No instance selected'}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#6b7280', fontSize: '0.9rem' }}>
                      Additional Hours
                    </label>
                    <input
                      type="number"
                      value={quotaHours}
                      onChange={(e) => setQuotaHours(e.target.value)}
                      placeholder="5"
                      min="1"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <button
                    onClick={handleAddQuota}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: 'linear-gradient(135deg, #003A70, #0066CC)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(79, 172, 254, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.3)';
                    }}
                  >
                    <i className="fas fa-plus"></i> Add Quota
                  </button>
                  <button
                    onClick={handleRemoveQuota}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: 'linear-gradient(135deg, #FF6600, #FF8533)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(255, 102, 0, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 102, 0, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 102, 0, 0.3)';
                    }}
                  >
                    <i className="fas fa-minus"></i> Remove Quota
                  </button>
                </div>
              </div>
            </div>
          )}        </div>

        {/* Instance Detail Modal */}
        {selectedInstance && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, color: '#003A70' }}>Instance Details</h2>
                <button 
                  onClick={() => setSelectedInstance(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div><strong>Instance ID:</strong><br /><code>{selectedInstance.InstanceId}</code></div>
                <div><strong>Public IP:</strong><br /><code>{selectedInstance.PublicIp || 'N/A'}</code></div>
                <div><strong>State:</strong><br />
                  <span className={`badge ${selectedInstance.State === 'running' ? 'badge-success' : 'badge-warning'}`}>
                    {selectedInstance.State === 'pending' || selectedInstance.State === 'stopping' || selectedInstance.State === 'starting' 
                      ? `⏳ ${selectedInstance.State}` 
                      : selectedInstance.State}
                  </span>
                </div>
                <div><strong>Type:</strong><br /><code>{selectedInstance.InstanceType}</code></div>
              </div>
              
              {selectedInstance.PublicIp && (
                <div style={{ background: '#003A70', color: '#f3f4f6', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', marginBottom: '1.5rem' }}>
                  <strong>SSH Command:</strong><br />
                  ssh -i CAB444-Admin-Tutorial-Key.pem ubuntu@{selectedInstance.PublicIp}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleDownloadKeypair(selectedInstance.InstanceId)}
                  style={{
                    background: 'linear-gradient(135deg, #003A70, #0066CC)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '0.75rem 1.5rem',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <i className="fas fa-key"></i> Download Keypair
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Instance Management Modal */}
        {selectedInstance && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, color: '#003A70' }}><i className="fas fa-sliders-h"></i> Instance Management</h2>
                <button 
                  onClick={() => setSelectedInstance(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div><strong>Instance ID:</strong><br /><code>{selectedInstance.InstanceId}</code></div>
                <div><strong>Name:</strong><br />{selectedInstance.Name}</div>
                <div><strong>Public IP:</strong><br /><code>{selectedInstance.PublicIp || 'N/A'}</code></div>
                <div><strong>State:</strong><br />
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    backgroundColor: selectedInstance.State === 'running' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 191, 36, 0.1)',
                    color: selectedInstance.State === 'running' ? '#22c55e' : '#fbbf24'
                  }}>
                    {selectedInstance.State}
                  </span>
                </div>
              </div>
              
              {selectedInstance.PublicIp && (
                <div style={{ 
                  background: '#003A70', 
                  color: '#f3f4f6', 
                  padding: '1rem', 
                  borderRadius: '12px', 
                  fontFamily: 'monospace', 
                  marginBottom: '1.5rem',
                  fontSize: '0.9rem'
                }}>
                  <strong>SSH Command:</strong><br />
                  ssh -i CAB444-Admin-Tutorial-Key.pem ubuntu@{selectedInstance.PublicIp}
                </div>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#374151' }}><i className="fas fa-shield-alt"></i> Add Your IP Address</h3>
                <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                  Your IP address has been auto-detected. Click "Add My IP" to allow SSH and RDP access.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={ipAddress}
                    onChange={(e) => setIpAddress(e.target.value)}
                    placeholder="Your IP will be auto-detected..."
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: '2px solid #F5F7FA',
                      borderRadius: '12px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      backgroundColor: ipAddress ? '#E6F3FF' : '#F5F7FA'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0066CC'}
                    onBlur={(e) => e.target.style.borderColor = '#F5F7FA'}
                  />
                  <button
                    onClick={handleAddIP}
                    disabled={ipLoading}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: ipLoading
                        ? 'linear-gradient(135deg, #F5F7FA, #d1d5db)'
                        : 'linear-gradient(135deg, #003A70, #0066CC)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: ipLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      boxShadow: ipLoading
                        ? 'none'
                        : '0 4px 12px rgba(79, 172, 254, 0.3)',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {ipLoading ? <><i className="fas fa-spinner fa-spin"></i> Adding...</> : <><i className="fas fa-plus-circle"></i> Add My IP</>}
                  </button>
                </div>
                <div style={{ 
                  marginTop: '0.5rem', 
                  fontSize: '0.8rem', 
                  color: '#6b7280',
                  background: 'rgba(59, 130, 246, 0.1)',
                  padding: '0.5rem',
                  borderRadius: '8px'
                }}>
                  <i className="fas fa-info-circle"></i> Your IP will be added as /32 (single IP) for SSH (port 22) and RDP (port 3389) access.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quota Management Modal */}
        {quotaInstance && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="glass-card" style={{
              borderRadius: '24px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, color: '#003A70' }}><i className="fas fa-hourglass-half"></i> Quota Management</h2>
                <button 
                  onClick={() => setQuotaInstance(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1.1rem' }}>Instance: {quotaInstance.Name}</h3>
                <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '0.85rem' }}>
                  Manage quota hours for this instance's assigned student
                </p>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#6b7280', fontSize: '0.85rem', fontWeight: '600' }}>
                    Instance ID
                  </label>
                  <div style={{
                    padding: '0.75rem',
                    border: '2px solid #F5F7FA',
                    borderRadius: '12px',
                    backgroundColor: '#f9fafb',
                    fontSize: '0.85rem',
                    color: '#374151',
                    fontFamily: 'monospace'
                  }}>
                    {quotaInstance?.InstanceId || 'No instance selected'}
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#6b7280', fontSize: '0.85rem', fontWeight: '600' }}>
                    Hours to Add/Remove
                  </label>
                  <input
                    type="number"
                    value={quotaHours}
                    onChange={(e) => setQuotaHours(e.target.value)}
                    placeholder="Enter hours (e.g., 5)"
                    min="1"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #F5F7FA',
                      borderRadius: '12px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0066CC'}
                    onBlur={(e) => e.target.style.borderColor = '#F5F7FA'}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <button
                    onClick={handleAddQuota}
                    style={{
                      padding: '0.875rem 1.5rem',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>➕</span> Add Hours
                  </button>
                  <button
                    onClick={handleRemoveQuota}
                    style={{
                      padding: '0.875rem 1.5rem',
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                    }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>➖</span> Remove Hours
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Students Modal */}
      {studentsModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))',
            borderRadius: '24px',
            padding: '0',
            maxWidth: '480px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            animation: 'slideUp 0.3s ease'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #003A70, #0066CC)',
              padding: '1.5rem 2rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ 
                  margin: 0, 
                  color: 'white',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <i className="fas fa-users"></i>
                  Team Members
                </h2>
                <p style={{
                  margin: '0.25rem 0 0 0',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.85rem'
                }}>
                  {studentsModal.instanceName}
                </p>
              </div>
              <button 
                onClick={() => setStudentsModal({ isOpen: false, instanceName: '', students: [] })}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem 2rem' }}>
              {/* Student Count Badge */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.25rem',
                padding: '0.75rem 1rem',
                background: 'linear-gradient(135deg, rgba(0, 58, 112, 0.08), rgba(0, 102, 204, 0.08))',
                borderRadius: '12px',
                border: '1px solid rgba(0, 58, 112, 0.1)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #003A70, #0066CC)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1.1rem'
                }}>
                  {studentsModal.students.length}
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#003A70', fontSize: '0.95rem' }}>
                    {studentsModal.students.length === 1 ? 'Student' : 'Students'} Assigned
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    Access to this instance
                  </div>
                </div>
              </div>

              {/* Student List */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                {studentsModal.students.map((student, idx) => {
                  const studentId = student.email.split('@')[0];
                  const initials = student.name 
                    ? student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                    : studentId.slice(-2).toUpperCase();
                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.6rem 0.75rem',
                        background: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#0066CC';
                        e.currentTarget.style.background = 'rgba(0, 102, 204, 0.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: `linear-gradient(135deg, ${
                          ['#FF6600', '#0066CC', '#10b981', '#8b5cf6', '#ec4899'][idx % 5]
                        }, ${
                          ['#FF8533', '#4A90E2', '#059669', '#7c3aed', '#db2777'][idx % 5]
                        })`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '0.85rem',
                        flexShrink: 0
                      }}>
                        {initials}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: '600',
                          color: '#1f2937',
                          fontSize: '0.9rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {student.name || studentId.toUpperCase()}
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#6b7280',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}>
                          <span style={{ fontWeight: '500', color: '#003A70' }}>{studentId.toUpperCase()}</span>
                          <span>•</span>
                          <span>{student.email}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '1rem 2rem 1.5rem',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setStudentsModal({ isOpen: false, instanceName: '', students: [] })}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #003A70, #0066CC)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 58, 112, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <i className="fas fa-check"></i>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={`${confirmDialog.action === 'start' ? 'Start' : confirmDialog.action === 'force-stop' ? 'Force Stop' : 'Stop'} Instance`}
        message={`Are you sure you want to ${confirmDialog.action === 'force-stop' ? 'FORCE STOP' : confirmDialog.action} this instance?${confirmDialog.action === 'force-stop' ? '\n\n⚠️ WARNING: This will forcefully terminate the instance without graceful shutdown!' : ''}\n\n${confirmDialog.instanceName}\n(${confirmDialog.instanceId})`}
        confirmText={confirmDialog.action === 'start' ? 'Start Instance' : confirmDialog.action === 'force-stop' ? '<i className="fas fa-exclamation-triangle"></i> Force Stop' : 'Stop Instance'}
        cancelText="Cancel"
        onConfirm={confirmAction}
        onCancel={cancelAction}
        type={confirmDialog.action === 'force-stop' || confirmDialog.action === 'stop' ? 'danger' : 'info'}
      />
    </div>
  );
};

export default AdminDashboard;
